import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useInventory } from '../../../contexts/InventoryContext';
import SearchableCategoryDropdown from '../../Common/SearchableCategoryDropdown';

const CategoryForm = ({ category, onSuccess, onCancel }) => {
  const { categories, createCategory, updateCategory } = useInventory();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    parent: '',
    parentLabel: '',
    requiresSerialNumber: false,
    requiresExpiryDate: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      parent: '',
      parentLabel: '',
      requiresSerialNumber: false,
      requiresExpiryDate: false
    });
    setErrors({});
  };

  useEffect(() => {
    if (category) {
      const parentObj = category.parent && typeof category.parent === 'object' ? category.parent : null;
      setFormData({
        code: category.code || '',
        name: category.name || '',
        description: category.description || '',
        parent: parentObj?._id || category.parent || '',
        parentLabel: parentObj?.code && parentObj?.name ? `${parentObj.code} - ${parentObj.name}` : '',
        requiresSerialNumber: category.requiresSerialNumber || false,
        requiresExpiryDate: category.requiresExpiryDate || false
      });
    } else {
      resetForm();
    }
  }, [category]);

  const validateForm = () => {
    const newErrors = {};

    // Code validation removed as requested

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { parentLabel, ...rest } = formData;
      const submitData = {
        ...rest,
        parent: formData.parent && formData.parent !== '' ? formData.parent : null
      };

      if (category) {
        await updateCategory(category._id, submitData);
      } else {
        await createCategory(submitData);
      }
      onSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  // IDs to exclude from parent picker (self + immediate children) to prevent obvious circular refs
  const parentExcludeIds = category
    ? [
        category._id,
        ...categories
          .filter(c => {
            const pid = c.parent?._id || c.parent;
            return pid && String(pid) === String(category._id);
          })
          .map(c => c._id)
      ]
    : [];

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  {category ? 'Edit Category' : 'Create New Category'}
                </h3>
              </div>
              <button
                onClick={onCancel}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white">
            <form onSubmit={handleSubmit} className="px-4 pt-3 pb-2">

              {errors.submit && (
                <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2">
                  <svg className="h-3.5 w-3.5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-red-800">{errors.submit}</p>
                </div>
              )}

              {/* Top row: Code (edit only) + Name */}
              <div className={`grid ${category ? 'grid-cols-2' : 'grid-cols-1'} gap-2 mb-3`}>
                {category && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Category Code</label>
                    <input
                      type="text"
                      value={formData.code}
                      readOnly
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-gray-100 cursor-not-allowed uppercase"
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400">Auto-generated</p>
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-gray-600 mb-1">Category Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Electronics"
                    className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  />
                  {errors.name && <p className="mt-0.5 text-[10px] text-red-600">{errors.name}</p>}
                </div>
              </div>

              {/* Parent Category */}
              <div className="mb-3">
                <label htmlFor="parent" className="block text-xs font-medium text-gray-600 mb-1">Parent Category</label>
                <SearchableCategoryDropdown
                  value={formData.parent}
                  displayLabel={formData.parentLabel}
                  onChange={(id, label) => {
                    setFormData(prev => ({ ...prev, parent: id, parentLabel: label || '' }));
                    if (errors.parent) setErrors(prev => ({ ...prev, parent: '' }));
                  }}
                  placeholder="Select parent category…"
                  limit={50}
                  menuPlacement="down"
                  excludeIds={parentExcludeIds}
                />
                <p className="mt-0.5 text-[10px] text-gray-400">Leave empty to create a root category</p>
              </div>

              {/* Description */}
              <div className="mb-3">
                <label htmlFor="description" className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of this category..."
                  className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none ${errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50'}`}
                />
                {errors.description && <p className="mt-0.5 text-[10px] text-red-600">{errors.description}</p>}
                <p className="mt-0.5 text-[10px] text-gray-400">{formData.description.length}/500</p>
              </div>


              {/* Footer Actions */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && (
                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  )}
                  {category ? 'Update Category' : 'Create Category'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryForm;